import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { careService } from "../../services/careService";
import {
  formatDate,
  processRecords,
  processResidentId,
  processResidentName,
  processSocialWorkerName,
  processStatusClass,
  recordLabel,
  recordSlug,
} from "./AssessmentsPage";

export default function AssessmentProcessDetailPage() {
  const { assessmentId } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    careService.assessmentProcesses
      .get(assessmentId)
      .then((data) => {
        if (!isMounted) return;
        setAssessment(data || null);
        setError("");
      })
      .catch((err) => {
        if (!isMounted) return;
        setAssessment(null);
        setError(err?.response?.data?.message || err?.message || "Unable to load assessment process.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [assessmentId]);

  if (loading) {
    return (
      <CareShell className="dashboard-content residents-page assessments-page">
        <Panel title="Assessment Process">Loading assessment process...</Panel>
      </CareShell>
    );
  }

  if (!assessment && !error) return <Navigate to="/assessments" replace />;
  const residentId = processResidentId(assessment);
  const residentLink = residentId ? `/residents/${residentId}#assessment` : "/residents";

  return (
    <CareShell className="dashboard-content residents-page assessments-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>{assessment?.name || assessment?.title || "Assessment process"}</h1>
          <p>{processResidentName(assessment)} assessment process workspace.</p>
        </div>
        <div className="residents-actions">
          <Link className="mini-btn" to="/assessments"><i className="ti ti-arrow-left" /> Assessments</Link>
          <Link className="btn primary" to={residentLink}><i className="ti ti-user" /> Resident</Link>
        </div>
      </div>
      {error ? <p className="form-error">{error}</p> : null}

      <div className="assessment-drawer-action-links assessment-page-action-links">
        <Link to={residentLink}><i className="ti ti-user" /> Resident</Link>
        <button className="success" type="button"><i className="ti ti-clipboard-plus" /> Add Assessment</button>
        <button type="button"><i className="ti ti-edit" /> Edit</button>
        <button className="danger" type="button"><i className="ti ti-trash" /> Delete</button>
        <button className="warning" type="button"><i className="ti ti-ban" /> Cancel</button>
      </div>

      <Panel title="Assessment Records">
        <div className="assessment-record-link-grid">
          {processRecords(assessment).map((record) => (
            <Link to={`/assessments/${assessment.id}/records/${recordSlug(record)}`} key={recordSlug(record)}>
              <i className="ti ti-file-description" />
              <span>{recordLabel(record)}</span>
            </Link>
          ))}
        </div>
      </Panel>

      <div className="assessment-detail-page-grid">
        <section className="assessment-drawer-section">
          <h3>Process Details</h3>
          <div className="assessment-detail-grid">
            <div><span>Start date</span><strong>{formatDate(assessment.startDate || assessment.processStartDate)}</strong></div>
            <div><span>End date</span><strong>{formatDate(assessment.endDate || assessment.processEndDate)}</strong></div>
            <div><span>Status</span><strong><em className={`pill ${processStatusClass(assessment.status)}`}>{assessment.status}</em></strong></div>
            <div><span>Patient type</span><strong>{assessment.patientType || assessment.type}</strong></div>
          </div>
        </section>

        <section className="assessment-drawer-section">
          <h3>Resident Details</h3>
          <div className="assessment-detail-grid">
            <div><span>Resident name</span><strong>{processResidentName(assessment)}</strong></div>
            <div><span>Resident type</span><strong>{(assessment.patientType || assessment.type) === "NEW" || (assessment.type || "").includes("NEW") ? "New Resident" : "Current Resident"}</strong></div>
          </div>
        </section>

        <section className="assessment-drawer-section">
          <h3>Social Worker</h3>
          <div className="assessment-detail-grid">
            <div><span>First name</span><strong>{assessment.socialWorkerFirstName || assessment.socialWorker?.firstName || processSocialWorkerName(assessment).split(" ")[0]}</strong></div>
            <div><span>Last name</span><strong>{assessment.socialWorkerLastName || assessment.socialWorker?.lastName || processSocialWorkerName(assessment).split(" ").slice(1).join(" ")}</strong></div>
            <div><span>Email</span><strong>{assessment.socialWorkerEmail || assessment.socialWorker?.email || "Not set"}</strong></div>
            <div><span>Phone</span><strong>{assessment.socialWorkerPhone || assessment.socialWorker?.phone || "Not set"}</strong></div>
            <div className="wide"><span>Address</span><strong>{assessment.socialWorkerAddress || assessment.socialWorker?.address || "Not set"}</strong></div>
          </div>
        </section>

        <section className="assessment-drawer-section">
          <h3>Next of Kin</h3>
          <div className="assessment-detail-grid">
            <div><span>First name</span><strong>{assessment.nextOfKinFirstName || assessment.nextOfKin?.firstName || "Not set"}</strong></div>
            <div><span>Last name</span><strong>{assessment.nextOfKinLastName || assessment.nextOfKin?.lastName || "Not set"}</strong></div>
            <div><span>Email</span><strong>{assessment.nextOfKinEmail || assessment.nextOfKin?.email || "Not set"}</strong></div>
            <div><span>Phone</span><strong>{assessment.nextOfKinPhone || assessment.nextOfKin?.phone || "Not set"}</strong></div>
            <div><span>Relationship</span><strong>{assessment.nextOfKinRelationship || assessment.nextOfKin?.relationship || "Not set"}</strong></div>
            <div><span>Address</span><strong>{assessment.nextOfKinAddress || assessment.nextOfKin?.address || "Not set"}</strong></div>
          </div>
        </section>
      </div>
    </CareShell>
  );
}
